import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playTone } from "@/lib/tones";

export function NotificationCentre() {
  const [d, setD] = useState({ items: [], unread: 0, pending_ack: 0 });
  const [tab, setTab] = useState("unread");
  const nav = useNavigate();
  const { user } = useAuth();
  const seen = useRef(null);
  const prefs = useRef(user?.prefs);
  prefs.current = user?.prefs || { tone: "chime", popup: true };
  const alertNew = useCallback((items) => {
    const fresh = items.filter((n) => !n.read && !seen.current.has(n.id));
    items.forEach((n) => seen.current.add(n.id));
    if (!fresh.length) return;
    if (prefs.current.tone !== "off") playTone(prefs.current.tone || "chime");
    if (prefs.current.popup === false) return;
    fresh.slice(0, 3).forEach((n) => toast(n.title, { description: n.body, duration: 7000, className: "notif-popup",
      action: n.link ? { label: "Open", onClick: () => nav(n.link) } : undefined }));
  }, [nav]);
  const load = useCallback(() => api.get("/notifications").then((r) => {
    if (seen.current === null) seen.current = new Set(r.data.items.map((n) => n.id));
    else alertNew(r.data.items);
    setD(r.data);
  }).catch(() => {}), [alertNew]);
  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden) load(); }, 20000); return () => clearInterval(t); }, [load]);
  const open = async (n) => { if (!n.important) await api.post(`/notifications/${n.id}/read`); await load(); if (n.link) nav(n.link); };
  const ack = async (n) => { await api.post(`/notifications/${n.id}/ack`); await load(); };
  const rows = d.items.filter((n) => (tab === "unread" ? !n.read || (n.important && !n.acknowledged) : n.read));
  return (
    <Popover>
      <PopoverTrigger data-testid="notifications-button" className="relative flex h-10 w-10 items-center justify-center rounded-full text-[#C5A46D] transition-colors hover:bg-white/5">
        <Bell className={`h-5 w-5 ${d.unread > 0 ? "bell-ring" : ""}`} />
        {d.unread > 0 && <span data-testid="notifications-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C5A46D] px-1 text-[10px] font-bold text-[#171923]">{d.unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-centre">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="font-bold">Notifications{d.pending_ack > 0 && <span className="ml-2 text-xs font-semibold text-amber-400">{d.pending_ack} need acknowledgement</span>}</div>
          <button data-testid="notifications-read-all" onClick={async () => { await api.post("/notifications/read-all"); load(); }} className="flex items-center gap-1 text-[11px] text-[#C5A46D]"><CheckCheck className="h-3.5 w-3.5" />Mark all read</button>
        </div>
        <div className="flex gap-1 px-3 pt-2">
          {[["unread", "Unread"], ["read", "Read"]].map(([k, l]) => <button key={k} data-testid={`notifications-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${tab === k ? "bg-[#C5A46D] text-[#171923]" : "text-zinc-400"}`}>{l}</button>)}
        </div>
        <div className="thin-scroll max-h-[60vh] overflow-y-auto p-2">
          {rows.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`} className={`mb-1 rounded-lg p-3 ${n.important && !n.acknowledged ? "border border-amber-500/40" : ""}`}>
              <button onClick={() => open(n)} className="block w-full text-left">
                <div className="flex items-center gap-1.5 text-sm font-bold">{n.important && !n.acknowledged && <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />}{n.title}</div>
                <div className="mt-0.5 line-clamp-3 text-xs text-zinc-400">{n.body}</div>
                <div className="mt-1 text-[10px] text-zinc-500">{new Date(n.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
              </button>
              {n.important && !n.acknowledged && <button data-testid={`notification-ack-${n.id}`} onClick={() => ack(n)} className="mt-2 rounded-full bg-[#C5A46D] px-3 py-1 text-[11px] font-bold text-[#171923]">Acknowledge</button>}
            </div>
          ))}
          {!rows.length && <div className="py-8 text-center text-xs text-zinc-500">{tab === "unread" ? "You're all caught up." : "No read notifications."}</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
